// THE ORG MODEL POLICY: models an account never runs, whichever key serves them (#7810).
//
// Measured, tesote 2026-11-01: `gpt-6.1-sol` cooled on its weekly quota and the
// `scout` / `debugging-qa` lanes fell through the job-class tier chain onto
// `gpt-6-luna`, a model the owner rejects ("luna sucks"). It built tesote.ai#3305. A
// per-route ladder (`llm-route-ladder.ts`) orders what to try. This list says what is
// never tried at all, on any route, any rung, any key.
//
// ENFORCED AS DATA, AT ONE SEAM. `applyModelDenyList` rewrites the pool's catalogue
// view so a denied model reads as PARKED (`active: false`) on every key. It runs inside
// `listPoolKeys` (`@developerz/db`), the read every resolution path shares: the worker
// claim, the in-process pick, the agents-manager session pick, the escalation probe
// and the preview. Every predicate downstream (`keyServesModel`, `bestTaggedModel`,
// `nextLiveModel`, `refuseKeyModel`) then refuses it with no deny-specific branch of its
// own. That covers a route pin, a ladder rung, a tier tag, a member default and an
// envelope `llm` pin alike, because each one is graded by those predicates.

import { z } from 'zod';
import type { BYOKKey, BYOKKeyModel } from '../entities/byok';

/**
 * At most this many entries. A deny list longer than an account's whole catalogue is
 * a pasted provider list, and walking it costs a scan per pool read.
 */
export const DENIED_MODELS_MAX = 50;

/**
 * The stored list. Each entry is a provider model id, matched by {@link modelDenied}.
 * Ids are unique, so a write that repeats one is refused at the edge instead of stored
 * twice.
 */
export const DeniedModelsSchema = z
  .array(z.string().trim().min(1).max(200))
  .max(DENIED_MODELS_MAX)
  .refine((ms) => new Set(ms).size === ms.length, {
    message: 'each model may appear at most once',
  });
export type DeniedModels = readonly string[];

/**
 * The model's bare name: the id after a gateway's `vendor/` namespace, without an
 * OpenRouter `:variant` suffix. `openai/gpt-6-luna:free` becomes `gpt-6-luna`.
 */
function bareModelName(model: string): string {
  const tail = model.slice(model.lastIndexOf('/') + 1);
  const colon = tail.indexOf(':');
  return (colon < 0 ? tail : tail.slice(0, colon)).toLowerCase();
}

/**
 * Is `model` denied by `denied`? An entry denies the model with that EXACT id. An entry
 * with no `/` also denies the same model under any gateway namespace or variant
 * (`gpt-6-luna` denies `openai/gpt-6-luna` and `openai/gpt-6-luna:free`), because an
 * owner who says "never luna" means the model, not one provider's spelling of it. An
 * entry WITH a `/` names one gateway's id and denies exactly that id.
 */
export function modelDenied(model: string, denied: DeniedModels): boolean {
  if (denied.length === 0) return false;
  const bare = bareModelName(model);
  return denied.some((entry) =>
    entry.includes('/') ? entry === model : entry.toLowerCase() === bare || entry === model,
  );
}

/**
 * The pool as resolution must see it under `denied`. Each key's denied catalogue rows
 * read as parked (`active: false`), and a key whose OWN `model` is denied but has no
 * catalogue row for it gains a parked row, so `refuseKeyModel` refuses the `Key` rung
 * instead of running it. Keys that carry no denied model are returned as the same
 * object. Pure.
 */
export function applyModelDenyList(keys: readonly BYOKKey[], denied: DeniedModels): BYOKKey[] {
  if (denied.length === 0) return [...keys];
  return keys.map((key) => denyOnKey(key, denied));
}

function denyOnKey(key: BYOKKey, denied: DeniedModels): BYOKKey {
  const models = key.models ?? [];
  let moved = false;
  const next: BYOKKeyModel[] = models.map((m) => {
    if (m.active === false || !modelDenied(m.model, denied)) return m;
    moved = true;
    return { ...m, active: false, denied: true };
  });
  if (modelDenied(key.model, denied) && !models.some((m) => m.model === key.model)) {
    moved = true;
    next.push({ model: key.model, tier: null, active: false, denied: true });
  }
  return moved ? { ...key, models: next } : key;
}
